import { initializeApp } from 'firebase/app'
import { getStorage, ref, uploadBytes as fsUploadBytes, getDownloadURL } from 'firebase/storage'
import { getFirestore } from 'firebase/firestore'
import {
	signOut,
	getAuth,
	signInWithEmailAndPassword as emailAndPassword,
	signInWithCustomToken as signInWithCustomTokenInFirebase,
	GoogleAuthProvider,
	signInWithPopup,
	OAuthProvider,
	Auth
} from 'firebase/auth'
import { firebaseConfig } from '../config'
import { clearScratchLocalStorage } from '../util/storage'

export const firebaseApp = initializeApp(firebaseConfig)
const googleProvider = new GoogleAuthProvider()

// Only allow @scratchpay.com domains to authenticate
googleProvider.setCustomParameters({
	hd: 'scratchpay.com',
	prompt: 'select_account'
})

export const authClient = () => {
	const auth = getAuth(firebaseApp)
	auth.tenantId = null // Ensure tenantId is cleared for the default auth client
	return auth
}

export const authClientWithTenant = (tenantId?: string) => {
	const auth = getAuth(firebaseApp)
	if (tenantId) {
		auth.tenantId = tenantId
	} else {
		auth.tenantId = null
	}
	return auth
}

export const storageClient = getStorage(firebaseApp, `gs://${firebaseConfig.storageBucket}`)

export const signInWithEmailAndPassword = (email, password) =>
	emailAndPassword(authClient(), email, password)

export const signInWithCustomToken = (token: string) =>
	signInWithCustomTokenInFirebase(authClient(), token)

export const logout = async () => {
	try {
		await signOut(authClient())
	} finally {
		// Always wipe cached state — even if signOut rejects — so the next user
		// of a shared terminal never sees the previous session's data.
		clearScratchLocalStorage()
	}
}

export const firestoreClient = () => getFirestore(firebaseApp)

export const uploadFromBlobAsync =
	(rootDirectory: string) =>
	async ({ blobUrl, name }) => {
		if (!blobUrl || !name) return null
		const blob = await fetch(blobUrl).then((r) => r.blob())
		const snapshot = await fsUploadBytes(ref(storageClient, `${rootDirectory}/${name}`), blob)
		return snapshot.ref
	}

export const getDownloadUrl = async (directoryPath) => {
	try {
		const fileRef = ref(storageClient, directoryPath)
		const url = await getDownloadURL(fileRef)
		return url
	} catch (error) {
		console.error('Error getting download URL:', error)
		return null
	}
}

export const signInWithGoogle = async () => signInWithPopup(authClient(), googleProvider)

export const signInWithMicrosoftTenant = async (client: Auth, microsoftTenantId: string) => {
	const provider = new OAuthProvider('microsoft.com')

	provider.setCustomParameters({
		tenant: microsoftTenantId,
		prompt: 'select_account'
	})

	provider.addScope('email')
	provider.addScope('profile')

	return signInWithPopup(client, provider)
}

export const AUTH_USER_NOT_FOUND_ERROR = 'auth/user-not-found'
export const AUTH_WRONG_PASSWORD_ERROR = 'auth/wrong-password'
